import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Database, Search, RefreshCw, Loader2, X, ChevronLeft, ChevronRight,
  SlidersHorizontal, Plus, Upload, Pencil, Trash2, Tags,
} from "lucide-react";

import {
  useGetDatasetItemsQuery,
  useGetDatasetFiltersQuery,
  useCreateDatasetEntryMutation,
  useUpdateDatasetEntryMutation,
  useDeleteDatasetEntryMutation,
  useImportDatasetMutation,
  useCreateDatasetCategoryMutation,
  useDeleteDatasetCategoryMutation,
  type DatasetRow,
  type DatasetCategory,
  type DatasetValueSpec,
} from "../api/datasetApi";
import { useAuth } from "../../../context/AuthContexts";

/**
 * One screen for every per-type databank register.
 *
 * The register is the `:dataset` route segment, and everything that differs
 * between registers arrives in the API's `meta`: what the register is called,
 * what its number is called, what the described thing is called, how many
 * levels of category it files under, and whether it splits into variants.
 * Adding unit rates or plant hire later is a route entry and a menu link; this
 * file does not change.
 *
 * VARIANTS. Labour rates is formal and informal — one register, one table, but
 * three levels of filing on one side and one on the other, "Description" against
 * "Operative / Worker", "Rate" against "Daily Rate", and separate category
 * lists. The screen shows one variant at a time because they are genuinely
 * different things to read, and because every index on the table leads with the
 * variant column: a list that mixed them could not use them.
 *
 * Paging is by cursor, so there are no page numbers and no total on the pager.
 * That is not an omission: counting rows is the query that would make this
 * screen slow at the volumes the register is heading for, so the API does not
 * count and the UI does not pretend to know. `meta.total_rows` in the header is
 * pre-computed for the WHOLE register and is labelled as such.
 */

const EMPTY = {
  state_id: "", zone: "", category_id: "", sub_category_id: "", sub_category_2_id: "",
  unit_id: "", measurement_id: "", year: "", month: "", q: "",
};
type Filters = typeof EMPTY;

const emptyForm = {
  state_id: "", zone: "", description: "", unit: "", measurement: "", code: "",
  category_id: "", sub_category_id: "", sub_category_2_id: "",
  year: String(new Date().getFullYear()), month: "",
};

/** The register's numbers as form fields, keyed by column. */
type ValueDraft = Record<string, string>;

function extractError(err: any) {
  return (
    err?.data?.message ||
    err?.data?.errors?.[Object.keys(err?.data?.errors ?? {})[0]]?.[0] ||
    "Something went wrong"
  );
}

export default function DatasetPage() {
  const { dataset = "composite_rates" } = useParams();
  const { user } = useAuth();

  const isAdmin = useMemo(() => {
    const names = ((user?.roles || []) as any[])
      .map((r) => (typeof r === "string" ? r : r?.name))
      .map((r) => String(r || "").toLowerCase());
    return names.some((r) => ["admin", "super_admin", "super admin"].includes(r));
  }, [user?.roles]);

  // Undefined until the API tells us which variants exist and which is default —
  // asking for one by name before that would be guessing at the register.
  const [variant, setVariant] = useState<string | undefined>(undefined);

  const [filters, setFilters] = useState<Filters>({ ...EMPTY });
  const [searchDraft, setSearchDraft] = useState("");
  // Cursor pagination has no page numbers, so "previous" is a stack of the
  // cursors already visited rather than an arithmetic step backwards.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);

  const cursor = cursorStack[cursorStack.length - 1];

  const patch = (p: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...p }));
    setCursorStack([null]);
  };

  // Switching register (or variant) resets everything downstream — the other
  // half's categories and cursors mean nothing here.
  useEffect(() => {
    setFilters({ ...EMPTY });
    setSearchDraft("");
    setCursorStack([null]);
  }, [dataset, variant]);

  const { data: meta } = useGetDatasetFiltersQuery({ dataset, admin: isAdmin, variant });
  const { data, isLoading, isFetching, refetch } = useGetDatasetItemsQuery({
    dataset, admin: isAdmin, variant, ...filters, cursor,
  });

  const rows = data?.data ?? [];
  const info = data?.meta ?? meta?.meta;
  const states = meta?.states ?? [];
  const zones = meta?.zones ?? [];
  const units = meta?.units ?? [];
  const categories = meta?.categories ?? [];
  const years = meta?.years ?? [];
  const months = meta?.months ?? [];
  const measurements = meta?.measurements ?? [];

  const depth = info?.taxonomy_depth ?? 2;
  // Labour outputs are not dated — a mason lays what a mason lays — so the
  // register says whether a year means anything here, and nothing about years
  // is rendered when it does not.
  const hasYear = info?.has_year ?? true;
  // The basic price list prices by month and carries a size/spec beside the
  // unit of sale; the rate registers do neither.
  const hasMonth = info?.has_month ?? false;
  const hasMeasurement = info?.has_measurement ?? false;
  const hasState = info?.has_state ?? true;
  // Infrastructure benchmarks are published per geopolitical zone (and
  // nationwide). A SECOND location beside the state, not a replacement for it:
  // an entry may carry a zone, a state, or both, so both are offered and
  // neither is required. The zone is free text and the options are the zones
  // the register holds, so the dropdown is empty until something is imported —
  // which is why the form offers a text box beside it.
  const hasZone = info?.has_zone ?? false;
  const hasCode = info?.has_code ?? false;
  // Most registers carry one number. Constants carries four, and the table and
  // the form are built from this list rather than from a single hard-coded one.
  const valueSpecs: DatasetValueSpec[] = useMemo(
    () => info?.values ?? [{ column: "value", label: info?.value_label ?? "Value", kind: info?.value_kind ?? "currency" }],
    [info?.values, info?.value_label, info?.value_kind],
  );
  const descriptionLabel = info?.description_label ?? "Description";
  const valueLabel = info?.value_label ?? "Value";
  // The variant actually in force, for writes — the API resolves the default
  // when we send none, and tells us which it picked.
  const activeVariant = info?.variant?.active ?? variant;

  const childrenOf = (list: DatasetCategory[], id: string) =>
    (list.find((c) => String(c.id) === id)?.children ?? []) as DatasetCategory[];

  const subCats = useMemo(
    () => childrenOf(categories, filters.category_id),
    [categories, filters.category_id],
  );
  const subCats2 = useMemo(
    () => childrenOf(subCats, filters.sub_category_id),
    [subCats, filters.sub_category_id],
  );

  /** Formats one of the register's numbers the way that number reads. */
  const fmt = (v: any, kind: DatasetValueSpec["kind"] = "currency") => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    if (kind === "ratio") return n.toFixed(3);
    if (kind === "quantity") return n.toLocaleString("en-NG", { maximumFractionDigits: 4 });
    return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Admin actions ────────────────────────────────────────────────────────
  const [createEntry, { isLoading: creating }] = useCreateDatasetEntryMutation();
  const [updateEntry, { isLoading: updating }] = useUpdateDatasetEntryMutation();
  const [deleteEntry] = useDeleteDatasetEntryMutation();
  const [importFile, { isLoading: importing }] = useImportDatasetMutation();
  const [createCategory] = useCreateDatasetCategoryMutation();
  const [deleteCategory] = useDeleteDatasetCategoryMutation();

  const [showForm, setShowForm] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [valueDraft, setValueDraft] = useState<ValueDraft>({});
  const [formError, setFormError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DatasetRow | null>(null);

  const setField = (k: keyof typeof emptyForm) => (e: any) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const formSubs = useMemo(
    () => childrenOf(categories, form.category_id),
    [categories, form.category_id],
  );
  const formSubs2 = useMemo(
    () => childrenOf(formSubs, form.sub_category_id),
    [formSubs, form.sub_category_id],
  );

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setValueDraft({});
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (r: DatasetRow) => {
    setEditId(r.id);
    setForm({
      state_id: r.state_id ? String(r.state_id) : "",
      zone: r.zone ?? "",
      description: r.description ?? "",
      unit: r.unit ?? "",
      measurement: r.measurement ?? "",
      code: r.code ?? "",
      category_id: r.category_id ? String(r.category_id) : "",
      sub_category_id: r.sub_category_id ? String(r.sub_category_id) : "",
      sub_category_2_id: r.sub_category_2_id ? String(r.sub_category_2_id) : "",
      year: r.year ? String(r.year) : "",
      month: r.month ? String(r.month) : "",
    });
    setValueDraft(
      Object.fromEntries(
        valueSpecs.map((v) => [v.column, r.values?.[v.column] == null ? "" : String(r.values[v.column])]),
      ),
    );
    setFormError(""); setShowForm(true);
  };

  const submit = async () => {
    setFormError("");

    const filled = valueSpecs.filter((v) => (valueDraft[v.column] ?? "") !== "");

    if (!form.description.trim() || !form.category_id || filled.length === 0) {
      setFormError(
        valueSpecs.length === 1
          ? `${descriptionLabel}, category and ${valueLabel.toLowerCase()} are required.`
          : `${descriptionLabel}, category and at least one of ${valueSpecs.map((v) => v.label).join(", ")} are required.`,
      );
      return;
    }

    const body: any = {
      description: form.description.trim(),
      unit: form.unit.trim() || null,
      category_id: Number(form.category_id),
      values: Object.fromEntries(
        valueSpecs.map((v) => [
          v.column,
          (valueDraft[v.column] ?? "") === "" ? null : Number(valueDraft[v.column]),
        ]),
      ),
    };
    if (hasState) body.state_id = form.state_id ? Number(form.state_id) : null;
    if (hasZone) body.zone = form.zone.trim() || null;
    if (hasCode) body.code = form.code.trim() || null;
    if (hasYear) body.year = form.year ? Number(form.year) : null;
    if (hasMonth) body.month = form.month ? Number(form.month) : null;
    if (hasMeasurement) body.measurement = form.measurement.trim() || null;
    // Levels the register does not file at are not sent at all — the API
    // rejects a sub-category on a register that files one level deep, and it is
    // right to.
    if (depth >= 2) body.sub_category_id = form.sub_category_id ? Number(form.sub_category_id) : null;
    if (depth >= 3) body.sub_category_2_id = form.sub_category_2_id ? Number(form.sub_category_2_id) : null;

    try {
      if (editId) await updateEntry({ dataset, variant: activeVariant, id: editId, body }).unwrap();
      else await createEntry({ dataset, variant: activeVariant, body }).unwrap();
      setBanner({ type: "success", text: editId ? "Entry updated." : "Entry added." });
      setShowForm(false);
    } catch (err: any) {
      // 409 is the register's natural key refusing a second rate for the same
      // item, unit, state and year. It is a normal thing to hit, not a fault,
      // so it is worded as a fact about the register rather than an error.
      setFormError(extractError(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEntry({ dataset, variant: activeVariant, id: deleteTarget.id }).unwrap();
      setBanner({ type: "success", text: "Entry deleted." });
    } catch (err) {
      setBanner({ type: "error", text: extractError(err) });
    }
    setDeleteTarget(null);
  };

  const onImport = async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res: any = await importFile({ dataset, variant: activeVariant, file: fd }).unwrap();
      const unresolved = res?.unresolved_locations ?? [];
      setBanner({
        type: "success",
        text: (res?.message || "Imported.") +
          (unresolved.length ? ` Unmatched locations: ${unresolved.slice(0, 5).join(", ")}` : ""),
      });
    } catch (err) {
      setBanner({ type: "error", text: extractError(err) });
    }
  };

  const inputCls =
    "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition";

  const chips = useMemo(() => {
    const out: { key: keyof Filters; label: string }[] = [];
    if (filters.q) out.push({ key: "q", label: `“${filters.q}”` });
    if (hasZone && filters.zone) out.push({ key: "zone", label: filters.zone });
    const st = hasState ? states.find((s) => String(s.id) === filters.state_id) : undefined;
    if (st) out.push({ key: "state_id", label: st.name });
    const cat = categories.find((c) => String(c.id) === filters.category_id);
    if (cat) out.push({ key: "category_id", label: cat.name });
    const sub = subCats.find((s) => String(s.id) === filters.sub_category_id);
    if (sub) out.push({ key: "sub_category_id", label: sub.name });
    const sub2 = subCats2.find((s) => String(s.id) === filters.sub_category_2_id);
    if (sub2) out.push({ key: "sub_category_2_id", label: sub2.name });
    const un = units.find((u) => String(u.id) === filters.unit_id);
    if (un) out.push({ key: "unit_id", label: un.name });
    const ms = measurements.find((m) => String(m.id) === filters.measurement_id);
    if (ms) out.push({ key: "measurement_id", label: ms.name });
    if (hasYear && filters.year) out.push({ key: "year", label: String(filters.year) });
    const mo = months.find((m) => String(m.id) === filters.month);
    if (mo) out.push({ key: "month", label: mo.name });
    return out;
  }, [filters, states, categories, subCats, subCats2, units, measurements, months, hasYear, hasState, hasZone]);

  const columnCount =
    3 + valueSpecs.length +
    (hasState ? 1 : 0) + (hasZone ? 1 : 0) + (hasCode ? 1 : 0) + (hasYear ? 1 : 0) + (hasMonth ? 1 : 0) +
    (hasMeasurement ? 1 : 0) + (depth >= 3 ? 1 : 0) + (isAdmin ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5">
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-blue-700 text-white p-6 md:p-8 shadow-lg shadow-indigo-200/50">
        <div className="absolute -top-16 -right-10 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="p-3 rounded-2xl bg-white/15 backdrop-blur border border-white/20 shrink-0">
              <Database className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-black tracking-tight break-words">
                {info?.label ?? "Register"}
              </h1>
              <p className="text-sm text-indigo-100/90 mt-0.5">
                {info?.total_rows
                  ? `${info.total_rows.toLocaleString()} entries on the register`
                  : "Reference data by state and year."}
                {info?.refreshed_at && (
                  <span className="text-indigo-200/70">
                    {" "}· count as of {new Date(info.refreshed_at).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <button
                  onClick={() => setShowCats(true)}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition"
                >
                  <Tags className="w-4 h-4" /> Categories
                </button>
                <label className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition cursor-pointer">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
                  <input
                    type="file"
                    accept=".csv,.txt,.xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onImport(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white text-indigo-700 hover:bg-indigo-50 text-sm font-bold transition"
                >
                  <Plus className="w-4 h-4" /> Add entry
                </button>
              </>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-sm font-semibold transition disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Variant tabs — only for a register that has them. An upload and an
            "Add entry" go into whichever tab is showing, which is why the tab
            sits here beside those buttons rather than down among the filters. */}
        {info?.variant && (
          <div className="relative mt-6 inline-flex items-center gap-1 p-1 rounded-xl bg-white/10 border border-white/20">
            {info.variant.options.map((o) => {
              const on = (activeVariant ?? info.variant!.active) === o.key;
              return (
                <button
                  key={o.key}
                  onClick={() => setVariant(o.key)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-bold transition ${
                    on ? "bg-white text-indigo-700" : "text-indigo-100 hover:bg-white/10"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {banner && (
        <div
          className={`rounded-xl px-4 py-3 text-sm flex items-start justify-between gap-3 ${
            banner.type === "success"
              ? "bg-emerald-50 border border-emerald-100 text-emerald-800"
              : "bg-rose-50 border border-rose-100 text-rose-800"
          }`}
        >
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3 text-gray-400">
          <SlidersHorizontal className="w-4 h-4" />
          <span className="text-[11px] font-black uppercase tracking-widest">Filter</span>
        </div>

        {/* One filter per row on a phone: these are selects with long option
            text (state names, category paths), and two to a row truncates both. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3">
          <div className="sm:col-span-2 md:col-span-4 relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && patch({ q: searchDraft })}
              onBlur={() => searchDraft !== filters.q && patch({ q: searchDraft })}
              placeholder={`Search ${descriptionLabel.toLowerCase()}… (press Enter)`}
              className={`${inputCls} pl-9`}
            />
          </div>

          {hasZone && zones.length > 0 && (
            <div className="md:col-span-2">
              <select value={filters.zone} onChange={(e) => patch({ zone: e.target.value })} className={inputCls}>
                <option value="">All zones</option>
                {zones.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          )}

          {hasState && (
            <div className="md:col-span-2">
              <select value={filters.state_id} onChange={(e) => patch({ state_id: e.target.value })} className={inputCls}>
                <option value="">All states</option>
                {states.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div className="md:col-span-2">
            <select
              value={filters.category_id}
              onChange={(e) => patch({ category_id: e.target.value, sub_category_id: "", sub_category_2_id: "" })}
              className={inputCls}
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>

          {depth >= 2 && (
            <div className="md:col-span-2">
              <select
                value={filters.sub_category_id}
                onChange={(e) => patch({ sub_category_id: e.target.value, sub_category_2_id: "" })}
                className={inputCls}
                disabled={!filters.category_id}
              >
                <option value="">All sub-categories</option>
                {subCats.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </div>
          )}

          {depth >= 3 && (
            <div className="md:col-span-2">
              <select
                value={filters.sub_category_2_id}
                onChange={(e) => patch({ sub_category_2_id: e.target.value })}
                className={inputCls}
                disabled={!filters.sub_category_id}
              >
                <option value="">All second-level</option>
                {subCats2.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div className="md:col-span-1">
            <select value={filters.unit_id} onChange={(e) => patch({ unit_id: e.target.value })} className={inputCls}>
              <option value="">Unit</option>
              {units.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
            </select>
          </div>

          {hasMeasurement && (
            <div className="md:col-span-2">
              <select
                value={filters.measurement_id}
                onChange={(e) => patch({ measurement_id: e.target.value })}
                className={inputCls}
              >
                <option value="">All sizes</option>
                {measurements.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
              </select>
            </div>
          )}

          {hasYear && (
            <div className="md:col-span-1">
              <select value={filters.year} onChange={(e) => patch({ year: e.target.value })} className={inputCls}>
                <option value="">Year</option>
                {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>
          )}

          {hasMonth && (
            <div className="md:col-span-2">
              <select value={filters.month} onChange={(e) => patch({ month: e.target.value })} className={inputCls}>
                <option value="">All months</option>
                {months.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {chips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-3">
            {chips.map((c) => (
              <button
                key={c.key}
                onClick={() => {
                  if (c.key === "q") setSearchDraft("");
                  patch({ [c.key]: "" } as Partial<Filters>);
                }}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full px-2.5 py-1"
              >
                {c.label} <X className="w-3 h-3" />
              </button>
            ))}
            <button
              onClick={() => { setSearchDraft(""); setFilters({ ...EMPTY }); setCursorStack([null]); }}
              className="text-[11px] font-semibold text-gray-500 hover:text-gray-700 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── The register ─────────────────────────────────────────────────
          Two renderings of the same rows. A register carries up to a dozen
          columns — zone, state, code, description, size, unit, category, two
          sub-levels, month, year, then the rates themselves — and no amount of
          horizontal scrolling makes that readable on a phone. So below `md`
          each row becomes a card: the description leads, the rates are stated
          plainly, and everything else is a labelled pair. */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        {/* Phone and tablet, up to a full-width desktop */}
        <div className="lg:hidden divide-y divide-gray-100">
          {isLoading && (
            <div className="px-4 py-16 text-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div className="px-4 py-16 text-center text-gray-400 text-sm">
              Nothing on the register for this selection.
            </div>
          )}

          {rows.map((r) => (
            <div key={r.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-snug">
                    {r.description ?? "—"}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-gray-400">
                    {hasCode && r.code && <span className="font-mono">{r.code}</span>}
                    {r.category && <span>{r.category}</span>}
                    {r.sub_category && <span>· {r.sub_category}</span>}
                    {depth >= 3 && r.sub_category_2 && <span>· {r.sub_category_2}</span>}
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(r)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* The figures — the reason anybody opened the register. */}
              <div className="grid grid-cols-2 gap-2">
                {valueSpecs.map((v) => (
                  <div key={v.column} className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      {v.label}
                    </div>
                    <div className="text-sm font-bold text-gray-900">
                      {fmt(r.values?.[v.column], v.kind)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Everything that qualifies the figure above. */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
                {r.unit && <span><span className="text-gray-400">Unit</span> {r.unit}</span>}
                {hasMeasurement && r.measurement && (
                  <span><span className="text-gray-400">Size</span> {r.measurement}</span>
                )}
                {hasZone && r.zone && <span><span className="text-gray-400">Zone</span> {r.zone}</span>}
                {hasState && r.state && <span><span className="text-gray-400">State</span> {r.state}</span>}
                {hasMonth && r.month_name && (
                  <span><span className="text-gray-400">Month</span> {r.month_name}</span>
                )}
                {hasYear && r.year && <span><span className="text-gray-400">Year</span> {r.year}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop.
            NOTHING SCROLLS SIDEWAYS. Two things make that true:

            · the secondary columns — zone, state, size, second level, month,
              year — drop out below `xl`, so there are fewer of them to fit;
            · every remaining cell WRAPS. A long description or a two-word
              category takes a second line inside its own column instead of
              pushing the table wider than the screen.

            `table-fixed` is what makes the wrapping hold: without it the
            browser widens columns to fit their longest word and the table
            overflows again however much text is allowed to wrap. */}
        <div className="hidden lg:block">
          <table className="w-full table-fixed text-sm break-words">
            <thead className="bg-gray-50 text-gray-500">
              <tr className="text-left">
                {/* Widths live on the header row because `table-fixed` reads
                    them from the first row. The description is left unsized so
                    it takes whatever the others do not — it is the column with
                    real text in it, and the one that should get the slack. */}
                {hasZone && (
                  <th className="hidden xl:table-cell w-24 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Zone</th>
                )}
                {hasState && (
                  <th className="hidden xl:table-cell w-28 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">State</th>
                )}
                {hasCode && (
                  <th className="w-28 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Code</th>
                )}
                <th className="px-4 py-3 font-bold text-[11px] uppercase tracking-wider">{descriptionLabel}</th>
                {hasMeasurement && (
                  <th className="hidden xl:table-cell w-24 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Size</th>
                )}
                <th className="w-20 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Unit</th>
                <th className="w-40 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Category</th>
                {depth >= 3 && (
                  <th className="hidden xl:table-cell w-32 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Second level</th>
                )}
                {hasMonth && (
                  <th className="hidden xl:table-cell w-24 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Month</th>
                )}
                {hasYear && (
                  <th className="hidden xl:table-cell w-16 px-4 py-3 font-bold text-[11px] uppercase tracking-wider">Year</th>
                )}
                {valueSpecs.map((v) => (
                  <th key={v.column} className="w-32 px-4 py-3 font-bold text-[11px] uppercase tracking-wider text-right">
                    {v.label}
                  </th>
                ))}
                {isAdmin && <th className="w-24 px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-16 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-16 text-center text-gray-400">
                    Nothing on the register for this selection.
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  {hasZone && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-700">{r.zone ?? "—"}</td>
                  )}
                  {hasState && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-700">{r.state ?? "—"}</td>
                  )}
                  {hasCode && (
                    <td className="px-4 py-3 align-top text-gray-500 font-mono text-xs">{r.code ?? "—"}</td>
                  )}
                  <td className="px-4 py-3 align-top text-gray-900">{r.description ?? "—"}</td>
                  {hasMeasurement && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.measurement ?? "—"}</td>
                  )}
                  <td className="px-4 py-3 align-top text-gray-500">{r.unit ?? "—"}</td>
                  <td className="px-4 py-3 align-top text-gray-500">
                    {r.category ?? "—"}
                    {r.sub_category && <span className="text-gray-400"> · {r.sub_category}</span>}
                  </td>
                  {depth >= 3 && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.sub_category_2 ?? "—"}</td>
                  )}
                  {hasMonth && (
                    <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.month_name ?? "—"}</td>
                  )}
                  {hasYear && <td className="hidden xl:table-cell px-4 py-3 align-top text-gray-500">{r.year ?? "—"}</td>}
                  {valueSpecs.map((v) => (
                    <td key={v.column} className="px-4 py-3 align-top text-right font-semibold text-gray-900">
                      {fmt(r.values?.[v.column], v.kind)}
                    </td>
                  ))}
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(r)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cursor pager — no page numbers, by design (see the file header). */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
          <span className="text-xs text-gray-500">
            {rows.length > 0 ? `${rows.length} entries on this page` : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s))}
              disabled={cursorStack.length <= 1 || isFetching}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <button
              onClick={() => data?.next_cursor && setCursorStack((s) => [...s, data.next_cursor])}
              disabled={!data?.has_more || isFetching}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm disabled:opacity-40"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Add / edit ──────────────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900">
                {editId ? "Edit entry" : `Add to ${info?.label ?? "register"}`}
                {info?.variant && (
                  <span className="text-gray-400 font-normal">
                    {" "}· {info.variant.options.find((o) => o.key === activeVariant)?.label}
                  </span>
                )}
              </h3>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-500" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              {formError && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-600">{descriptionLabel}</label>
                <textarea
                  value={form.description}
                  onChange={setField("description")}
                  rows={2}
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {hasZone && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Zone</label>
                    {/* A list to pick from AND a box to type in: the options are
                        the zones already on the register, so picking keeps the
                        spelling consistent, and typing is what lets the first
                        entry for a new zone exist at all. */}
                    <input
                      value={form.zone}
                      onChange={setField("zone")}
                      list="databank-zones"
                      placeholder="e.g. North-West"
                      className={inputCls}
                    />
                    <datalist id="databank-zones">
                      {zones.map((z) => <option key={z} value={z} />)}
                    </datalist>
                  </div>
                )}

                {hasState && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">State</label>
                    <select value={form.state_id} onChange={setField("state_id")} className={inputCls}>
                      <option value="">—</option>
                      {states.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {hasCode && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Code</label>
                    <input value={form.code} onChange={setField("code")} placeholder="e.g. C1.02" className={inputCls} />
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-gray-600">Unit</label>
                  <input
                    value={form.unit}
                    onChange={setField("unit")}
                    list="dataset-units"
                    placeholder="M3, Bag, Day…"
                    className={inputCls}
                  />
                  <datalist id="dataset-units">
                    {units.map((u) => <option key={u.id} value={u.name} />)}
                  </datalist>
                </div>
                {hasMeasurement && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Size / spec</label>
                    <input
                      value={form.measurement}
                      onChange={setField("measurement")}
                      list="dataset-measurements"
                      placeholder="50kg, 12mm…"
                      className={inputCls}
                    />
                    <datalist id="dataset-measurements">
                      {measurements.map((m) => <option key={m.id} value={m.name} />)}
                    </datalist>
                  </div>
                )}

                {hasYear && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Year</label>
                    <input value={form.year} onChange={setField("year")} inputMode="numeric" className={inputCls} />
                  </div>
                )}

                {hasMonth && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Month</label>
                    <select value={form.month} onChange={setField("month")} className={inputCls}>
                      <option value="">—</option>
                      {months.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-gray-600">Category</label>
                  <select
                    value={form.category_id}
                    onChange={(e) => setForm((p) => ({
                      ...p, category_id: e.target.value, sub_category_id: "", sub_category_2_id: "",
                    }))}
                    className={inputCls}
                  >
                    <option value="">—</option>
                    {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                  </select>
                </div>

                {depth >= 2 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Sub-category</label>
                    <select
                      value={form.sub_category_id}
                      onChange={(e) => setForm((p) => ({
                        ...p, sub_category_id: e.target.value, sub_category_2_id: "",
                      }))}
                      className={inputCls}
                      disabled={!form.category_id}
                    >
                      <option value="">—</option>
                      {formSubs.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {depth >= 3 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Second-level sub-category</label>
                    <select
                      value={form.sub_category_2_id}
                      onChange={setField("sub_category_2_id")}
                      className={inputCls}
                      disabled={!form.sub_category_id}
                    >
                      <option value="">—</option>
                      {formSubs2.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {valueSpecs.map((v) => (
                  <div key={v.column}>
                    <label className="text-xs font-semibold text-gray-600">{v.label}</label>
                    <input
                      value={valueDraft[v.column] ?? ""}
                      onChange={(e) => setValueDraft((p) => ({ ...p, [v.column]: e.target.value }))}
                      inputMode="decimal"
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border text-sm">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={creating || updating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-60"
              >
                {(creating || updating) && <Loader2 className="w-4 h-4 animate-spin" />}
                {editId ? "Save changes" : "Add entry"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Categories ──────────────────────────────────────────────────── */}
      {showCats && (
        <CategoriesPanel
          title={`Categories — ${info?.label ?? dataset}${
            info?.variant ? ` · ${info.variant.options.find((o) => o.key === activeVariant)?.label}` : ""
          }`}
          categories={categories}
          maxDepth={depth}
          onClose={() => setShowCats(false)}
          onCreate={(name, parent_id) =>
            createCategory({ dataset, variant: activeVariant, name, parent_id }).unwrap()}
          onDelete={(id) => deleteCategory({ dataset, variant: activeVariant, id }).unwrap()}
        />
      )}

      {/* ── Delete confirm ──────────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Delete this entry?</h3>
            <p className="text-sm text-gray-500">{deleteTarget.description}</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg border text-sm">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Category editor, recursive to whatever depth the register files at.
 *
 * One component rather than one per level: composite rates files two deep and
 * formal labour three, and informal labour one, and hard-coding that would mean
 * three editors that drift apart.
 */
function CategoriesPanel({
  title, categories, maxDepth, onClose, onCreate, onDelete,
}: {
  title: string;
  categories: DatasetCategory[];
  maxDepth: number;
  onClose: () => void;
  onCreate: (name: string, parentId?: number) => Promise<any>;
  onDelete: (id: number) => Promise<any>;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const add = async (value: string, parentId?: number) => {
    setError("");
    if (!value.trim()) return;
    try {
      await onCreate(value.trim(), parentId);
      if (!parentId) setName("");
    } catch (e) {
      setError(extractError(e));
    }
  };

  const remove = (node: DatasetCategory) => {
    if (window.confirm(`Delete "${node.name}" and everything filed under it? Entries using it will show as unassigned.`)) {
      onDelete(node.id).catch((e) => setError(extractError(e)));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add(name)}
              placeholder="New category…"
              className="flex-1 border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <button onClick={() => add(name)} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium">
              Add
            </button>
          </div>

          {error && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>
          )}

          <div className="divide-y border rounded-lg">
            {categories.length === 0 && (
              <div className="p-4 text-sm text-gray-400 text-center">No categories yet.</div>
            )}
            {categories.map((c) => (
              <TaxonomyNode
                key={c.id}
                node={c}
                depth={0}
                maxDepth={maxDepth}
                onAdd={add}
                onRemove={remove}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TaxonomyNode({
  node, depth, maxDepth, onAdd, onRemove,
}: {
  node: DatasetCategory;
  depth: number;
  maxDepth: number;
  onAdd: (name: string, parentId?: number) => Promise<any>;
  onRemove: (node: DatasetCategory) => void;
}) {
  const [draft, setDraft] = useState("");
  const children = node.children ?? [];
  // A register that files two levels deep gets no "add" box at level two.
  const canNest = depth + 1 < maxDepth;

  return (
    <div className={depth === 0 ? "p-3 space-y-2" : "space-y-1"}>
      <div className="flex items-center gap-2">
        <span className={`flex-1 ${depth === 0 ? "text-sm font-semibold text-gray-800" : "text-sm text-gray-600"}`}>
          {node.name}
        </span>
        {canNest && children.length > 0 && (
          <span className="text-[10px] text-gray-400">{children.length}</span>
        )}
        <button onClick={() => onRemove(node)} className="p-1 text-rose-400 hover:bg-rose-50 rounded">
          <Trash2 className={depth === 0 ? "w-3.5 h-3.5" : "w-3 h-3"} />
        </button>
      </div>

      {canNest && (
        <div className="pl-3 space-y-1 border-l border-gray-100">
          {children.map((child) => (
            <TaxonomyNode
              key={child.id}
              node={child}
              depth={depth + 1}
              maxDepth={maxDepth}
              onAdd={onAdd}
              onRemove={onRemove}
            />
          ))}
          <div className="flex gap-2 pt-1">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { onAdd(draft, node.id); setDraft(""); }
              }}
              placeholder="Add under this…"
              className="flex-1 border rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <button
              onClick={() => { onAdd(draft, node.id); setDraft(""); }}
              className="px-3 py-1.5 rounded-lg border text-xs font-medium"
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
